import NextHead from 'next/head';
import { useRouter } from 'next/router';
import Script from 'next/script';
import alternativeSites from '@99designs/acquisition-frontend/acquisition/lib/alternativeSites';

interface Props {
    title: string;
    description: string;
    currentDomain: string;
    robots?: string;
    addSEOTags?: boolean;
    children?: React.ReactNode;
}

const genericHreflangDomains: Record<string, string> = {
    '99designs.com': 'en',
    '99designs.de': 'de',
    '99designs.fr': 'fr',
    '99designs.es': 'es',
    '99designs.it': 'it',
    '99designs.jp': 'ja',
    '99designs.com.br': 'pt',
    '99designs.nl': 'nl',
};

function getCanonicalForSubDomain(subDomain: string): string {
    const lang = subDomain.substring(0, 2);
    let canonicalDomain = '99designs.com';
    switch (lang) {
        case 'fr':
            canonicalDomain = '99designs.fr';
            break;
        case 'es':
            canonicalDomain = '99designs.es';
            break;
        case 'it':
            canonicalDomain = '99designs.it';
            break;
        default:
            break;
    }
    return canonicalDomain;
}

function getSEOTags(currentDomain: string, path: string) {
    const isSubDomain = currentDomain.includes('.99designs.');
    const ccTLDs = alternativeSites.filter((siteData) => !siteData[1].includes('.99designs.'));
    if (isSubDomain) {
        return (
            <link
                rel="canonical"
                href={`https://${getCanonicalForSubDomain(currentDomain)}${path}`}
            />
        );
    }
    return (
        <>
            <link rel="canonical" href={`https://${currentDomain}${path}`} />
            <link rel="alternate" hrefLang={'x-default'} href={`https://99designs.com${path}`} />
            {ccTLDs.map(([locale, domain]) => {
                const hrefVal = genericHreflangDomains[domain] || locale;
                return (
                    <link
                        rel="alternate"
                        hrefLang={hrefVal}
                        key={locale}
                        href={`https://${domain}${path}`}
                    />
                );
            })}
        </>
    );
}

export function Head({
    title,
    description,
    robots = '',
    currentDomain,
    addSEOTags = false,
    children,
}: Props) {
    const router = useRouter();
    const pathWithoutQueryParams = router.asPath.split('?')[0];
    const baseUrl = `https://${currentDomain}${pathWithoutQueryParams}`;

    return (
        <>
            <NextHead>
                <title>{title} | 99designs</title>
                <meta name="description" content={description} />
                <meta property="og:title" content={title} />
                <meta property="og:description" content={description} />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={`${baseUrl}`} />

                {robots && <meta name="robots" content={robots} />}

                {addSEOTags && getSEOTags(currentDomain, pathWithoutQueryParams)}

                <meta name="viewport" content="width=device-width, initial-scale=1" />
                <meta httpEquiv="cleartype" content="on" />
                <meta httpEquiv="X-UA-Compatible" content="IE=edge,chrome=1" />
                <meta httpEquiv="X-UA-TextLayoutMetrics" content="natural" />
                <meta httpEquiv="Accept-CH" content="DPR, Viewport-Width, Width" />

                {children}
            </NextHead>
            <Script src="/accounts/accounts.js" strategy="beforeInteractive" defer />
        </>
    );
}
